import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import type { AuthSession, Language, LoginDestination } from '../../types';
import { _t, languageDirection } from '../../lib/i18n';
import { api } from '../../lib/api';
import { normalizeEmailInput } from '../../lib/normalizeEmail';
import { useStorefrontAuth } from '../../hooks/useStorefrontAuth';
import { staffHomePath } from '../../lib/tenantPermissions';
import { appPathWithTenant } from '../../lib/tenantHost';
import { dialogFieldClass } from '../common/AppDialog';
import { headerPillPrimaryClass, radiusControlClass } from '../../styles/uiStandards';

type VisitingCardLoginScreenProps = {
  lang: Language;
  tenantSlug: string;
  tenantName?: string;
  onBack: () => void;
  onLoggedIn: () => void;
};


type LoginResult =
  | { session: AuthSession }
  | { chooseDestination: true; pickToken: string; destinations: LoginDestination[] };

/**
 * Full-bleed login inside the visiting-card phone shell (not the website dialog).
 */
export function VisitingCardLoginScreen({
  lang,
  tenantSlug,
  tenantName,
  onBack,
  onLoggedIn,
}: VisitingCardLoginScreenProps) {
  const navigate = useNavigate();
  const { completeDestination, refresh } = useStorefrontAuth(tenantSlug);
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [pickToken, setPickToken] = useState<string | null>(null);
  const [destinations, setDestinations] = useState<LoginDestination[] | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onBack]);

  const mapError = (
    err: unknown,
    context: 'password' | 'otp_request' | 'otp_verify' = 'password',
  ): string => {
    const statusCode = (err as { status?: number }).status;
    const code = (err as { data?: { error?: string } }).data?.error;
    if (code === 'otp_cooldown' || code === 'otp_rate_limited') {
      return _t('login.otpRateLimited', lang);
    }
    if (code === 'otp_expired') return _t('login.otpExpired', lang);
    if (code === 'otp_locked') return _t('login.otpLocked', lang);
    if (code === 'otp_send_failed' || code === 'otp_request_failed') {
      return _t('login.otpSendFailed', lang);
    }
    if (code === 'invalid_code') return _t('login.otpInvalidCode', lang);
    if (code === 'platform_access_disabled') {
      return _t('login.platformAccessDisabled', lang);
    }
    if (code === 'destination_forbidden' || code === 'invalid_pick_token') {
      return _t('login.destinationFailed', lang);
    }
    if (statusCode === 404 || code === 'not_found' || code === 'tenant_not_found') {
      return _t('login.notFound', lang);
    }
    if (context === 'otp_request') return _t('login.otpSendFailed', lang);
    if (context === 'otp_verify') return _t('login.otpInvalidCode', lang);
    return _t('login.invalidCredentials', lang);
  };

  const navigateAfterStaffLogin = (session: AuthSession) => {
    if (session.type === 'platform') {
      navigate('/platform/admin', { replace: true });
      return;
    }
    if (session.type === 'tenant_user' && session.tenantSlug) {
      navigate(appPathWithTenant(staffHomePath(session), session.tenantSlug), { replace: true });
    }
  };

  const finishSession = async (session: AuthSession) => {
    if (session.type === 'client') {
      if (session.tenantSlug && session.tenantSlug !== tenantSlug) {
        setError(_t('storefront.loginWrongTenant', lang));
        return;
      }
      await refresh();
      onLoggedIn();
      return;
    }
    if (session.type === 'tenant_user' || session.type === 'platform') {
      await refresh();
      onLoggedIn();
      navigateAfterStaffLogin(session);
    }
  };

  const resolveLoginResult = async (result: LoginResult) => {
    if ('chooseDestination' in result && result.chooseDestination === true) {
      const match = result.destinations.find(
        (d) => d.kind === 'tenant' && d.slug === tenantSlug,
      );
      if (match) {
        const session = await completeDestination(result.pickToken, match.key);
        await finishSession(session);
        return;
      }
      setPickToken(result.pickToken);
      setDestinations(result.destinations);
      return;
    }
    if ('session' in result) {
      await finishSession(result.session);
    }
  };

  const onPickDestination = async (key: string) => {
    if (busy || !pickToken) return;
    setBusy(true);
    setError(null);
    try {
      const session = await completeDestination(pickToken, key);
      setPickToken(null);
      setDestinations(null);
      await finishSession(session);
    } catch (err) {
      setError(mapError(err, 'password'));
      setPickToken(null);
      setDestinations(null);
    } finally {
      setBusy(false);
    }
  };

  const onRequestOtp = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const normalizedEmail = normalizeEmailInput(email);
      if (normalizedEmail !== email) setEmail(normalizedEmail);
      await api.requestLoginOtp(normalizedEmail, tenantSlug, lang);
      setOtpSent(true);
      setStatus(_t('login.otpSent', lang));
    } catch (err) {
      setError(mapError(err, 'otp_request'));
    } finally {
      setBusy(false);
    }
  };

  const onSubmitOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const normalizedEmail = normalizeEmailInput(email);
      const result = await api.verifyLoginOtp(normalizedEmail, otpCode, tenantSlug);
      await resolveLoginResult(result);
    } catch (err) {
      setError(mapError(err, 'otp_verify'));
    } finally {
      setBusy(false);
    }
  };

  const fieldClass = `${dialogFieldClass} w-full`;
  const dir = languageDirection(lang);
  const picking = Boolean(pickToken && destinations?.length);

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-[var(--bg-primary,#f4f1ec)] text-[var(--text-primary)]"
      dir={dir}
    >
      <header className="flex shrink-0 items-center gap-2 border-b border-[var(--border-color)] px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          disabled={busy}
          className={`inline-flex items-center gap-0.5 text-sm font-medium text-[var(--accent-primary)] disabled:opacity-50`}
          aria-label={_t('visitingCard.loginBack', lang)}
        >
          <ChevronLeft size={18} aria-hidden className={dir === 'rtl' ? 'rotate-180' : ''} />
          {_t('visitingCard.loginBack', lang)}
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5">
        <h1 className="text-xl font-semibold tracking-tight">
          {_t('common.login', lang)}
        </h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          {_t('storefront.loginHint', lang).replace(
            '{slug}',
            tenantName?.trim() || tenantSlug,
          )}
        </p>

        {picking ? (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-[var(--text-secondary)]">
              {_t('login.destinationPickerHint', lang)}
            </p>
            <ul className="flex flex-col gap-2">
              {(destinations || []).map((dest) => {
                const label = dest.kind === 'platform'
                  ? _t('login.destinationPlatform', lang)
                  : dest.kind === 'partner'
                    ? _t('login.destinationPartner', lang)
                    : dest.name;
                return (
                  <li key={dest.key}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void onPickDestination(dest.key)}
                      className={`w-full border border-[var(--border-color)] bg-[var(--bg-secondary,#fff)] px-3 py-3 text-start text-sm font-medium disabled:opacity-50 ${radiusControlClass}`}
                    >
                      {label}
                      {dest.slug ? (
                        <span className="mt-0.5 block text-xs font-normal text-[var(--text-secondary)]">
                          {dest.slug}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
            {error ? (
              <p className="text-sm text-rose-600" role="alert">{error}</p>
            ) : null}
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPickToken(null);
                setDestinations(null);
                setError(null);
              }}
              className="w-full text-center text-sm text-[var(--text-secondary)] underline-offset-2 hover:underline"
            >
              {_t('common.cancel', lang)}
            </button>
          </div>
        ) : (
          <form
            onSubmit={otpSent ? onSubmitOtp : onRequestOtp}
            className="mt-5 space-y-3"
          >
            <label className="block space-y-1 text-sm">
              <span className="text-[var(--text-secondary)]">{_t('common.email', lang)}</span>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setOtpSent(false);
                  setOtpCode('');
                  setStatus(null);
                }}
                onBlur={() => setEmail((v) => normalizeEmailInput(v))}
                className={fieldClass}
              />
            </label>

            {otpSent ? (
              <label className="block space-y-1 text-sm">
                <span className="text-[var(--text-secondary)]">{_t('login.otpCode', lang)}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={fieldClass}
                />
              </label>
            ) : null}

            {status ? (
              <p className="text-sm text-[var(--text-secondary)]">{status}</p>
            ) : null}
            {error ? (
              <p className="text-sm text-rose-600" role="alert">{error}</p>
            ) : null}

            <button
              type="submit"
              disabled={
                busy
                || !email.trim()
                || (otpSent && otpCode.length < 6)
              }
              className={`w-full ${headerPillPrimaryClass} h-10 disabled:opacity-50`}
            >
              {busy
                ? _t('common.loading', lang)
                : otpSent
                  ? _t('login.otpVerify', lang)
                  : _t('login.otpSend', lang)}
            </button>

            {otpSent ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void onRequestOtp()}
                className="w-full text-center text-sm text-[var(--accent-primary)] underline-offset-2 hover:underline"
              >
                {_t('login.otpResend', lang)}
              </button>
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
}
